<template>
  <div class="workplace-game">
    <LevelComplete v-if="gameOver" />

    <div v-if="!gameOver && currentProfession" class="game-content">
      <h2 class="question">
        Where does
        <span @click="speakOutLoud(professionWithArticle)">
          {{ professionWithArticle }} {{ currentProfession.emoji }}
        </span>
        work?
      </h2>

      <div class="options">
        <div
          v-for="workspace in currentOptions"
          :key="workspace.name"
          class="workspace-tile"
          :class="{
            correct: selectedCorrect === workspace.name,
            incorrect: selectedIncorrect.includes(workspace.name),
          }"
          :style="{ backgroundImage: 'url(' + workspace.image + ')' }"
          @click="handleSelection(workspace)"
        >
          <div class="label">{{ workspace.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import LevelComplete from "../LevelComplete.vue";
import { useSpeechSynthesis } from "@/composables/useSpeechSynthesis";

export default {
  components: { LevelComplete },
  setup() {
    const { speakOutLoud } = useSpeechSynthesis();

    const workspaces = [
      {
        name: "Hospital",
        label: "Hospital",
        image:
          "https://static.vecteezy.com/system/resources/previews/004/493/181/non_2x/hospital-building-for-healthcare-background-illustration-with-ambulance-car-doctor-patient-nurses-and-medical-clinic-exterior-free-vector.jpg",
      },
      {
        name: "Kitchen",
        label: "Kitchen",
        image:
          "https://img.freepik.com/free-vector/kitchen-appliance-concept-illustration_114360-27219.jpg?semt=ais_items_boosted&w=740",
      },
      {
        name: "Construction",
        label: "Construction Site",
        image:
          "https://static.vecteezy.com/system/resources/previews/006/607/727/non_2x/construction-site-concept-with-drill-excavator-and-material-equipment-illustration-vector.jpg",
      },
      {
        name: "Classroom",
        label: "Classroom",
        image:
          "https://img.freepik.com/free-vector/empty-classroom-interior-school-college-class_107791-631.jpg?semt=ais_hybrid&w=740",
      },
      {
        name: "Studio",
        label: "Art Studio",
        image:
          "https://img.freepik.com/free-vector/hand-drawn-art-studio-workplace-illustration_23-2148057200.jpg",
      },
    ];

    const professions = [
      { name: "Doctor", workspace: "Hospital", emoji: "👩‍⚕️" },
      { name: "Chef", workspace: "Kitchen", emoji: "👨‍🍳" },
      { name: "Engineer", workspace: "Construction", emoji: "👷" },
      { name: "Teacher", workspace: "Classroom", emoji: "👩‍🏫" },
      { name: "Artist", workspace: "Studio", emoji: "🎨" },
    ];

    const professionIndex = ref(0);
    const gameOver = ref(false);
    const currentOptions = ref([]);
    const selectedCorrect = ref(null);
    const selectedIncorrect = ref([]);
    const timeoutIds = [];

    const currentProfession = computed(() => professions[professionIndex.value] || null);

    const getIndefiniteArticle = (word = "") => {
      return /^[aeiou]/i.test(word.trim()) ? "an" : "a";
    };

    const professionWithArticle = computed(() => {
      if (!currentProfession.value) return "";
      const name = currentProfession.value.name.toLowerCase();
      return `${getIndefiniteArticle(name)} ${name}`;
    });

    const shuffleArray = (array) => {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    const generateOptions = () => {
      if (!currentProfession.value) {
        currentOptions.value = [];
        return;
      }

      const correct = workspaces.find(
        (w) => w.name === currentProfession.value.workspace
      );

      if (!correct) {
        currentOptions.value = [];
        return;
      }

      const incorrect = shuffleArray(
        workspaces.filter((w) => w.name !== correct.name)
      ).slice(0, 2);

      currentOptions.value = shuffleArray([...incorrect, correct]);
      selectedCorrect.value = null;
      selectedIncorrect.value = [];
    };

    const isCorrect = (workspace) => {
      return (
        currentProfession.value &&
        workspace.name === currentProfession.value.workspace
      );
    };

    const playSound = async (url) => {
      try {
        const audio = new Audio(url);
        await audio.play();
      } catch (error) {
        console.warn("Audio playback failed:", error);
      }
    };

    const setSafeTimeout = (callback, delay) => {
      const id = window.setTimeout(callback, delay);
      timeoutIds.push(id);
      return id;
    };

    const speakCurrentQuestion = () => {
      if (!currentProfession.value) return;
      speakOutLoud(`Where does ${professionWithArticle.value} work?`);
    };

    const handleSelection = (workspace) => {
      if (!currentProfession.value || selectedCorrect.value) return;

      if (isCorrect(workspace)) {
        selectedCorrect.value = workspace.name;
        speakOutLoud(workspace.name);
        playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );

        setSafeTimeout(() => {
          professionIndex.value++;

          if (professionIndex.value >= professions.length) {
            gameOver.value = true;
            speakOutLoud("Great job!");

            setSafeTimeout(() => {
              window.location.href = "/professions";
            }, 4000);
          } else {
            generateOptions();
            speakCurrentQuestion();
          }
        }, 1200);
      } else if (!selectedIncorrect.value.includes(workspace.name)) {
        selectedIncorrect.value.push(workspace.name);
        speakOutLoud("Try again");
      }
    };

    onMounted(() => {
      generateOptions();
      speakCurrentQuestion();
    });

    onBeforeUnmount(() => {
      timeoutIds.forEach((id) => clearTimeout(id));
    });

    return {
      currentProfession,
      currentOptions,
      professionWithArticle,
      handleSelection,
      gameOver,
      selectedCorrect,
      selectedIncorrect,
      speakOutLoud,
    };
  },
};
</script>

<style scoped>
.workplace-game {
  padding: 16px;
  max-width: 480px;
  margin: 0 auto;
  text-align: center;
}

.question {
  font-size: 1.5rem;
  margin-bottom: 16px;
  background: #fff;
  padding: 10px;
  border-radius: 12px;
  cursor: pointer;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.workspace-tile {
  position: relative;
  height: 140px;
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s, border 0.2s;
  border: 4px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.workspace-tile.correct {
  border-color: #4caf50;
}

.workspace-tile.incorrect {
  border-color: #f44336;
}

.label {
  background: rgba(255, 255, 255, 0.85);
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 1rem;
  text-align: center;
  max-width: 90%;
  word-wrap: break-word;
}

/* Mobile responsiveness */
@media (max-width: 480px) {
  .workspace-tile {
    height: 120px;
  }

  .label {
    font-size: 0.95rem;
  }
}
</style>